// Benepack OMS · GL-12 自助改密 Modal (Topbar 用户菜单「修改密码」)
// RHF + Zod (ChangePasswordSchema · 旧密码 / 新密码[8-128·≥1字母1数字] / 确认一致)
//
// ★ 成功语义: 后端已吊销该用户全部 session (含当前会话) → 前端必须同步
//   AuthProvider.logout() + 跳 /login (别留死 token 撞 401)。
//   失败 (旧密码错/弱密码/同旧密码) 由 axios interceptor toast · Modal 保持打开可重试。
import { useState } from 'react';
import { Alert, Form, Input, Modal, message } from 'antd';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChangePasswordSchema, type ChangePasswordInput } from '@/zod/password';
import { changePassword } from '@/api/identity';
import { useAuth } from '@/hooks/use-auth';

export interface ChangePasswordModalProps {
  open: boolean;
  onClose: () => void;
}

export function ChangePasswordModal({ open, onClose }: ChangePasswordModalProps) {
  const { t } = useTranslation('common');
  const { logout } = useAuth();
  const nav = useNavigate();
  const [submitting, setSubmitting] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ChangePasswordInput>({
    resolver: zodResolver(ChangePasswordSchema),
    defaultValues: { old_password: '', new_password: '', confirm_password: '' },
  });

  const close = () => {
    reset();
    onClose();
  };

  const onSubmit = handleSubmit(async (values) => {
    setSubmitting(true);
    try {
      // 严格只发 proto ChangePasswordRequest 2 字段 (confirm 仅前端校验用)
      await changePassword({
        old_password: values.old_password,
        new_password: values.new_password,
      });
      message.success(t('password.changeOk'));
      close();
      // 后端已吊销全部 session (含当前) · 同步登出清本地三键 → 重新登录
      await logout();
      nav('/login', { replace: true });
    } catch {
      // axios interceptor 已 toast (旧密码错 401 / 弱密码 422 / 同旧密码 422) · 保持 Modal 可重试
    } finally {
      setSubmitting(false);
    }
  });

  return (
    <Modal
      title={t('password.changeTitle')}
      open={open}
      onOk={() => void onSubmit()}
      onCancel={close}
      confirmLoading={submitting}
      okText={t('password.submit')}
      destroyOnHidden
    >
      <Alert
        type="info"
        showIcon
        message={t('password.reloginHint')}
        style={{ marginBottom: 16 }}
      />
      <Form layout="vertical" onFinish={() => void onSubmit()}>
        <Form.Item
          label={t('password.old')}
          required
          validateStatus={errors.old_password ? 'error' : ''}
          help={errors.old_password?.message}
        >
          <Controller
            name="old_password"
            control={control}
            render={({ field }) => (
              <Input.Password
                {...field}
                autoComplete="current-password"
                placeholder={t('password.oldPlaceholder')}
              />
            )}
          />
        </Form.Item>
        <Form.Item
          label={t('password.new')}
          required
          validateStatus={errors.new_password ? 'error' : ''}
          help={errors.new_password?.message}
        >
          <Controller
            name="new_password"
            control={control}
            render={({ field }) => (
              <Input.Password
                {...field}
                autoComplete="new-password"
                placeholder={t('password.newPlaceholder')}
              />
            )}
          />
        </Form.Item>
        <Form.Item
          label={t('password.confirm')}
          required
          validateStatus={errors.confirm_password ? 'error' : ''}
          help={errors.confirm_password?.message}
        >
          <Controller
            name="confirm_password"
            control={control}
            render={({ field }) => (
              <Input.Password
                {...field}
                autoComplete="new-password"
                placeholder={t('password.confirmPlaceholder')}
              />
            )}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}
